import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Card, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDownload, IconTrash } from '@tabler/icons-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { httpRequest } from '@/shared/api/client';

export const Route = createFileRoute('/_app/settings/privacy')({ component: PrivacyPage });

function PrivacyPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const requestExport = async () => {
    try {
      const data = await httpRequest<Record<string, unknown>>('/api/account/export-personal-data', { method: 'GET', endpointType: 'plain' });
      // Download del file JSON localmente
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `personal-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      notifications.show({ color: 'green', message: 'Download avviato.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore durante l\'export';
      notifications.show({ color: 'red', title: 'Export fallito', message: msg });
    }
  };

  const askDelete = () =>
    modals.openConfirmModal({
      title: 'Eliminare il mio account',
      children: (
        <Stack gap="xs">
          <Text size="sm">Questa azione è irreversibile. Tutti i tuoi dati personali verranno cancellati o anonimizzati come da normativa GDPR.</Text>
          <Text size="sm">Le aziende a cui appartieni manterranno i dati aggregati storici.</Text>
        </Stack>
      ),
      labels: { confirm: 'Elimina account', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await httpRequest('/api/account/me', { method: 'DELETE', endpointType: 'plain' });
          notifications.show({ color: 'green', message: 'Account eliminato' });
          await logout();
          void navigate({ to: '/login' });
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Errore durante l\'eliminazione';
          notifications.show({ color: 'red', title: 'Eliminazione fallita', message: msg });
        }
      },
    });

  return (
    <Stack>
      <Title order={2}>Privacy / GDPR</Title>
      <Alert color="blue" icon={<IconAlertTriangle size={18} />}>
        Hai il diritto di accedere ai tuoi dati personali, riceverli in formato portabile e richiederne la cancellazione (art. 15-22 GDPR).
      </Alert>
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Title order={4}>Esporta i miei dati</Title>
          <Text size="sm" c="dimmed">Riceverai via email un archivio ZIP con tutti i dati personali registrati sul tuo account.</Text>
          <Button variant="light" leftSection={<IconDownload size={16} />} onClick={() => void requestExport()}>
            Richiedi export
          </Button>
        </Stack>
      </Card>
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Title order={4} c="red">Elimina il mio account</Title>
          <Text size="sm" c="dimmed">Azione irreversibile. Dopo l'eliminazione non potrai più accedere al servizio.</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={askDelete}>
            Elimina il mio account
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
}
